import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import { useTrackedState } from '@context/store';

import { useCexTokens } from '@views/Swap/context/providers/CexTokensProvider';

import { findProvider } from '@helpers/utils';

import { useDexTokens } from './DexTokensProvider';

type ExchangeRatesType = Record<string, Record<string, number>>;
type ExchangeRatesPayload = {
  baseCcy: string;
  quoteCcy: string;
  price: number;
};

const POLL_INTERVAL_MS = 60000;

const MARKETS_API_URL =
  process.env.GATSBY_MARKETS_API_URL ||
  'https://market-api.virginia.wallet.api.wombat.systems';

const ratesReducer = (
  state: ExchangeRatesType,
  { baseCcy, quoteCcy, price }: ExchangeRatesPayload,
): ExchangeRatesType => ({
  ...state,
  [baseCcy]: {
    ...state?.[baseCcy],
    [quoteCcy]: price,
  },
});

type AssetEntry = {
  assetIdentifier: string;
  blockchain: string;
  abbr: string;
};

// Staking tokens not present in the swap token lists — always fetch their rates.
const SUPPLEMENTAL_ASSETS: AssetEntry[] = [
  {
    assetIdentifier: '0x18084fbA666a33d37592fA2633fD49a74DD93a88',
    blockchain: 'ETH_BLOCKCHAIN',
    abbr: 'TBTC',
  },
];

const normalizeBlockchain = (blockchain: string): string => {
  if (blockchain.includes('_BLOCKCHAIN')) return blockchain;
  // WETH is on ETH, not its own chain
  if (blockchain === 'WETH') return 'ETH_BLOCKCHAIN';
  return `${blockchain}_BLOCKCHAIN`;
};

const toAssetEntry = (t: ITickerObject): AssetEntry => {
  const abbr = t.abbr.toUpperCase();
  const contractMatch = t.token?.match(/^(0x[a-fA-F0-9]+)/);
  const assetIdentifier = contractMatch ? contractMatch[1] : abbr;
  const blockchain = normalizeBlockchain(t.blockchain);
  return { assetIdentifier, blockchain, abbr };
};

const ExchangeRatesContext = createContext<ExchangeRatesType>({});

const ExchangeRatesProvider: FC<PropsWithChildren> = ({ children }) => {
  const { provider } = useTrackedState();
  const cexTokens = useCexTokens();
  const dexTokens = useDexTokens();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [rates, dispatchRates] = useReducer(ratesReducer, {});

  const tokens = useMemo(() => {
    const selectedProvider = findProvider(provider);
    return selectedProvider.isDeFi ? dexTokens : cexTokens;
  }, [provider, dexTokens, cexTokens]);

  useEffect(() => {
    const seen = new Set<string>();
    const assets: AssetEntry[] = [];

    const tokenEntries = tokens
      .filter(t => t.abbr.toUpperCase() !== 'USD')
      .map(toAssetEntry);

    [...tokenEntries, ...SUPPLEMENTAL_ASSETS].forEach(entry => {
      const key = `${entry.assetIdentifier}_${entry.blockchain}`;
      if (!seen.has(key)) {
        seen.add(key);
        assets.push(entry);
      }
    });

    const fetchAndUpdate = async () => {
      try {
        const response = await fetch(`${MARKETS_API_URL}/v1/market/rates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            assets: assets.map(({ assetIdentifier, blockchain }) => ({
              assetIdentifier,
              blockchain,
            })),
            ccy: 'usd',
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        (data.rates || []).forEach((rateData: any) => {
          const match = assets.find(
            a =>
              a.assetIdentifier.toLowerCase() ===
                rateData.assetId?.assetIdentifier?.toLowerCase() &&
              a.blockchain === rateData.assetId?.blockchain,
          );
          if (match && rateData.rate != null) {
            dispatchRates({
              baseCcy: match.abbr,
              quoteCcy: 'USD',
              price: rateData.rate,
            });
          }
        });

        // keep USD/USD = 1 for DexRatesProvider consumers
        dispatchRates({ baseCcy: 'USD', quoteCcy: 'USD', price: 1 });
      } catch (err) {
        console.warn('Failed to fetch exchange rates:', err);
      }
    };

    fetchAndUpdate();
    intervalRef.current = setInterval(fetchAndUpdate, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [tokens]);

  return (
    <ExchangeRatesContext.Provider value={rates}>
      {children}
    </ExchangeRatesContext.Provider>
  );
};

export const useExchangeRates = () => useContext(ExchangeRatesContext);
export default ExchangeRatesProvider;
